import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { io } from 'socket.io-client';
import { RefreshCw, Check, X, AlertTriangle, Clock } from 'lucide-react';

// =====================================================================
// PLENARIUM:MUDANCA_VOTO
//  - MudarMeuVoto (tablet do vereador): votacoes ja encerradas nesta
//    sessao, com o voto dele e o botao "Mudar meu voto" -> o pedido vai
//    para o Presidente.
//  - AutorizarMudancaVoto (tablet do Presidente e tela da sessao do
//    admin): pedidos pendentes com Autorizar / Recusar. Se a mudanca
//    virar o resultado, pede confirmacao antes.
// =====================================================================

const ROT = { SIM: 'SIM', NAO: 'NÃO', ABSTENCAO: 'ABSTENÇÃO' };
const COR = { SIM: ['#15803d', '#dcfce7'], NAO: ['#b91c1c', '#fee2e2'], ABSTENCAO: ['#475569', '#e2e8f0'] };
const RES = { APROVADA: 'Aprovada', REPROVADA: 'Reprovada' };
const erroDe = (e, padrao) => (e && e.response && e.response.data && e.response.data.error) || padrao;

const CSS = `
.pmv { background: #fff; border: 1px solid #e2e8f0; border-radius: 18px; padding: 14px 16px; margin-top: 14px; box-shadow: 0 4px 16px rgba(15,23,42,.05); color: #0f172a; text-align: left; }
.pmv-tit { display: flex; align-items: center; gap: 8px; font-weight: 800; font-size: 1.02rem; }
.pmv-sub { color: #64748b; font-size: .86rem; margin: 2px 0 8px; }
.pmv-item { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 10px 0; border-top: 1px solid #f1f5f9; }
.pmv-item-txt { flex: 1 1 200px; min-width: 0; }
.pmv-item-txt b { display: block; font-size: .95rem; overflow-wrap: anywhere; }
.pmv-item-txt small { color: #64748b; font-size: .82rem; }
.pmv-chip { display: inline-flex; align-items: center; gap: 4px; border-radius: 999px; padding: 3px 10px; font-size: .78rem; font-weight: 800; }
.pmv-bt { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 44px; padding: 0 14px; border-radius: 12px; border: 1px solid #cbd5e1; background: #fff; color: #0f172a; font: inherit; font-weight: 800; font-size: .92rem; cursor: pointer; }
.pmv-bt[disabled] { opacity: .5; cursor: default; }
.pmv-bt.azul { background: #1d4ed8; border-color: #1d4ed8; color: #fff; }
.pmv-bt.verde { background: #15803d; border-color: #15803d; color: #fff; }
.pmv-fundo { position: fixed; inset: 0; z-index: 9600; background: rgba(15,23,42,.55); display: flex; align-items: center; justify-content: center; padding: 16px; }
.pmv-modal { background: #fff; border-radius: 20px; padding: 20px; width: 100%; max-width: 480px; color: #0f172a; max-height: 90vh; overflow-y: auto; }
.pmv-modal h3 { margin: 0 0 4px; font-size: 1.15rem; }
.pmv-opcoes { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin: 14px 0; }
.pmv-op { min-height: 64px; border-radius: 14px; border: 3px solid transparent; font: inherit; font-weight: 900; font-size: 1.15rem; cursor: pointer; }
.pmv-op.ativo { border-color: #0f172a; }
.pmv-modal textarea { width: 100%; box-sizing: border-box; min-height: 70px; border: 1px solid #cbd5e1; border-radius: 12px; padding: 10px; font: inherit; resize: vertical; }
.pmv-flutua { position: fixed; left: 50%; transform: translateX(-50%); bottom: calc(84px + env(safe-area-inset-bottom)); z-index: 9400; width: min(560px, calc(100vw - 24px)); background: #fff; border: 2px solid #f59e0b; border-radius: 18px; box-shadow: 0 12px 40px rgba(15,23,42,.3); padding: 12px 14px; color: #0f172a; max-height: 60vh; overflow-y: auto; }
.pmv-ped { padding: 8px 0; border-top: 1px solid #f1f5f9; }
.pmv-ped:first-of-type { border-top: 0; }
.pmv-vira { margin-top: 6px; background: #fee2e2; color: #991b1b; border-radius: 10px; padding: 6px 10px; font-size: .85rem; font-weight: 800; display: flex; align-items: center; gap: 6px; }
.pmv-mantem { margin-top: 6px; color: #15803d; font-size: .82rem; font-weight: 700; }
`;

function useAvisos(sessaoId, aoMudar) {
  useEffect(() => {
    if (!sessaoId) return undefined;
    const s = io(process.env.REACT_APP_API_URL || 'http://localhost:3001');
    s.on('connect', () => { s.emit('entrar-sala', `sessao-${sessaoId}`); aoMudar(); });
    s.on('mudanca-voto', aoMudar);
    s.on('pauta-encerrada', aoMudar);
    const voltou = () => { if (document.visibilityState === 'visible') aoMudar(); };
    document.addEventListener('visibilitychange', voltou);
    return () => { s.disconnect(); document.removeEventListener('visibilitychange', voltou); };
  }, [sessaoId, aoMudar]);
}

const Voto = ({ op }) => { const [c, f] = COR[op] || COR.ABSTENCAO; return <span className="pmv-chip" style={{ color: c, background: f }}>{ROT[op] || op}</span>; };

// ---------------------------------------------------------------------
// Vereador
export function MudarMeuVoto({ sessao }) {
  const sessaoId = sessao && sessao.status === 'EM_ANDAMENTO' ? sessao.id : null;
  const [dados, setDados] = useState(null);
  const [modal, setModal] = useState(null); // { item, opcao, motivo }
  const [ocupado, setOcupado] = useState(false);
  const carregar = useCallback(() => {
    if (!sessaoId) return;
    axios.get(`/api/mudanca-voto/sessao/${sessaoId}/meus-votos`).then(r => setDados(r.data)).catch(() => {});
  }, [sessaoId]);
  useEffect(() => { setDados(null); carregar(); }, [carregar]);
  useAvisos(sessaoId, carregar);

  if (!sessaoId || !dados || !dados.aberta || !dados.itens.length) return null;

  const enviar = async () => {
    setOcupado(true);
    try {
      await axios.post(`/api/mudanca-voto/pauta/${modal.item.pautaId}`, { opcao: modal.opcao, motivo: modal.motivo });
      toast.success('Pedido enviado ao Presidente.'); setModal(null); carregar();
    } catch (e) { toast.error(erroDe(e, 'Não foi possível enviar.')); }
    setOcupado(false);
  };
  const cancelar = async (p) => {
    try { await axios.delete(`/api/mudanca-voto/${p.id}`); toast.success('Pedido cancelado.'); carregar(); }
    catch (e) { toast.error(erroDe(e, 'Não foi possível cancelar.')); }
  };

  return (
    <div className="pmv">
      <style>{CSS}</style>
      <div className="pmv-tit"><RefreshCw size={18} color="#1d4ed8" /> Mudar meu voto</div>
      <p className="pmv-sub">Votações já encerradas nesta sessão. A mudança precisa da autorização do Presidente.</p>
      {dados.itens.map(it => {
        const p = it.pedido;
        return (
          <div key={it.pautaId} className="pmv-item">
            <span className="pmv-item-txt">
              <b>{it.numero}{it.emBloco ? ` + ${it.emBloco} em bloco` : ''} · {RES[it.status] || it.status}</b>
              <small>{it.titulo}</small>
              <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <small>Seu voto:</small> <Voto op={it.meuVoto} />
                {p && p.status === 'PENDENTE' && <span className="pmv-chip" style={{ color: '#b45309', background: '#fef3c7' }}><Clock size={12} /> Pedido para {ROT[p.votoNovo]} aguardando o Presidente</span>}
                {p && p.status === 'APROVADO' && p.votoNovo === it.meuVoto && <span className="pmv-chip" style={{ color: '#15803d', background: '#dcfce7' }}><Check size={12} /> Mudança autorizada</span>}
                {p && p.status === 'RECUSADO' && <span className="pmv-chip" style={{ color: '#b91c1c', background: '#fee2e2' }}><X size={12} /> Pedido recusado{p.obs ? `: ${p.obs}` : ''}</span>}
              </div>
            </span>
            {p && p.status === 'PENDENTE'
              ? <button type="button" className="pmv-bt" onClick={() => cancelar(p)}><X size={16} /> Cancelar pedido</button>
              : <button type="button" className="pmv-bt azul" onClick={() => setModal({ item: it, opcao: null, motivo: '' })}><RefreshCw size={16} /> Mudar meu voto</button>}
          </div>
        );
      })}

      {modal && (
        <div className="pmv-fundo" onClick={() => !ocupado && setModal(null)}>
          <div className="pmv-modal" onClick={e => e.stopPropagation()}>
            <style>{CSS}</style>
            <h3>Mudar meu voto · {modal.item.numero}</h3>
            <p className="pmv-sub">{modal.item.titulo}</p>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}><small>Voto atual:</small> <Voto op={modal.item.meuVoto} /></div>
            {modal.item.emBloco > 0 && <p className="pmv-sub" style={{ marginTop: 8 }}>Votação em bloco: a mudança vale para as {modal.item.emBloco + 1} matérias.</p>}
            <div className="pmv-opcoes">
              {['SIM', 'NAO', 'ABSTENCAO'].filter(o => o !== modal.item.meuVoto).map(o => {
                const [c, f] = COR[o];
                return <button key={o} type="button" className={`pmv-op ${modal.opcao === o ? 'ativo' : ''}`} style={{ color: c, background: f }} onClick={() => setModal(m => ({ ...m, opcao: o }))}>{ROT[o]}</button>;
              })}
            </div>
            <textarea placeholder="Motivo (opcional). Ex.: votei errado por engano." value={modal.motivo} maxLength={1000} onChange={e => setModal(m => ({ ...m, motivo: e.target.value }))} />
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button type="button" className="pmv-bt" style={{ flex: 1 }} disabled={ocupado} onClick={() => setModal(null)}>Voltar</button>
              <button type="button" className="pmv-bt azul" style={{ flex: 2 }} disabled={ocupado || !modal.opcao} onClick={enviar}>{ocupado ? 'Enviando…' : 'Pedir ao Presidente'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Presidente (modo="tablet": cartao flutuante) e admin (modo="admin")
export function AutorizarMudancaVoto({ sessaoId, modo = 'tablet', meuId = null }) {
  const [lista, setLista] = useState([]);
  const [ocupado, setOcupado] = useState(false);
  const carregar = useCallback(() => {
    if (!sessaoId) { setLista([]); return; }
    axios.get(`/api/mudanca-voto/sessao/${sessaoId}`).then(r => setLista(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [sessaoId]);
  useEffect(() => { carregar(); }, [carregar]);
  useAvisos(sessaoId, carregar);

  // no tablet do Presidente, o pedido dele mesmo vai para o admin
  const pend = lista.filter(p => p.status === 'PENDENTE' && !(modo === 'tablet' && meuId && p.vereadorId === meuId));
  if (!sessaoId || !pend.length) return null;

  const decidir = async (p, decisao, confirmarVirada = false) => {
    let obs = '';
    if (decisao === 'RECUSAR') { const m = window.prompt('Motivo da recusa (opcional, o vereador vê):', ''); if (m === null) return; obs = m; }
    setOcupado(true);
    try {
      await axios.patch(`/api/mudanca-voto/${p.id}`, { decisao, confirmarVirada, obs });
      toast.success(decisao === 'APROVAR' ? 'Mudança de voto autorizada.' : 'Pedido recusado.');
      carregar();
    } catch (e) {
      const d = e.response && e.response.data;
      if (e.response && e.response.status === 409 && d && d.vira) {
        setOcupado(false);
        if (window.confirm(`${d.error}\n\nNovo placar: SIM ${d.previa.depois.sim} · NÃO ${d.previa.depois.nao} · ABST ${d.previa.depois.abs}.\n\nConfirmar a mudança e o novo resultado?`)) return decidir(p, decisao, true);
        return;
      }
      toast.error(erroDe(e, 'Não foi possível concluir.')); carregar();
    }
    setOcupado(false);
  };

  const corpo = pend.map(p => {
    const pv = p.previa;
    return (
      <div key={p.id} className="pmv-ped">
        <div style={{ fontWeight: 800 }}>Ver. {p.vereadorNome} · {p.pauta ? p.pauta.numero : 'Matéria'}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
          <Voto op={p.votoAnterior} /> <span style={{ fontWeight: 900 }}>→</span> <Voto op={p.votoNovo} />
          {p.pauta && <small style={{ color: '#64748b' }}>{p.pauta.titulo}</small>}
        </div>
        {p.motivo && <div style={{ fontSize: '.85rem', color: '#475569', marginTop: 4 }}>Motivo: {p.motivo}</div>}
        {pv && (pv.vira
          ? <div className="pmv-vira"><AlertTriangle size={16} /> Muda o resultado: {RES[pv.resultadoAntes]} → {RES[pv.resultadoDepois]} (SIM {pv.depois.sim} · NÃO {pv.depois.nao})</div>
          : <div className="pmv-mantem">Resultado continua {RES[pv.resultadoAntes] || ''} (SIM {pv.depois.sim} · NÃO {pv.depois.nao} · ABST {pv.depois.abs})</div>)}
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button type="button" className="pmv-bt verde" style={{ flex: 1 }} disabled={ocupado} onClick={() => decidir(p, 'APROVAR')}><Check size={16} /> Autorizar</button>
          <button type="button" className="pmv-bt" style={{ flex: 1, color: '#b91c1c' }} disabled={ocupado} onClick={() => decidir(p, 'RECUSAR')}><X size={16} /> Recusar</button>
        </div>
      </div>
    );
  });

  if (modo === 'admin') {
    return (
      <div className="card" style={{ marginBottom: 16, padding: '12px 16px', borderLeft: '3px solid #f59e0b' }}>
        <style>{CSS}</style>
        <h2 style={{ fontSize: '0.95rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}><RefreshCw size={16} /> Pedidos de mudança de voto ({pend.length})</h2>
        <p style={{ fontSize: '0.72rem', color: 'var(--texto-suave)', margin: '2px 0 6px' }}>O vereador pediu para mudar o voto numa votação já encerrada. O Presidente também pode autorizar pelo tablet.</p>
        {corpo}
      </div>
    );
  }
  return (
    <div className="pmv-flutua" role="alertdialog" aria-label="Pedidos de mudança de voto">
      <style>{CSS}</style>
      <div className="pmv-tit" style={{ marginBottom: 6 }}><RefreshCw size={18} color="#b45309" /> Pedido de mudança de voto</div>
      {corpo}
    </div>
  );
}
